import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { pathFor, useLink, usePage, useT } from "@/lib/i18n";

const LangSwitch = ({ className = "" }) => {
  const { lang, page } = usePage();
  const t = useT();
  return (
    <div className={`flex items-center rounded-full border border-forest/20 p-1 text-xs font-semibold ${className}`} role="group" aria-label="Language">
      {["tr", "sl"].map((l) => (
        <Link
          key={l}
          to={pathFor(l, page === "notfound" ? "home" : page)}
          hrefLang={l}
          lang={l}
          aria-current={l === lang ? "true" : undefined}
          aria-label={l === lang ? l.toUpperCase() : t.nav.switchTo}
          onClick={() => localStorage.setItem("his_lang", l)}
          data-testid={`lang-switch-${l}`}
          className={`rounded-full px-3 py-1.5 tracking-widest transition-colors duration-300 ${l === lang ? "bg-forest text-ivory" : "text-forest hover:bg-forest/10"}`}
        >
          {l.toUpperCase()}
        </Link>
      ))}
    </div>
  );
};

const IndividualsMenu = () => {
  const t = useT();
  const link = useLink();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="nav-individuals-btn"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 py-2 text-[15px] text-forest transition-colors hover:text-gold-text"
      >
        {t.nav.individuals} <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 w-[340px] -translate-x-1/2 pt-3"
          >
            <div className="card-soft border border-forest/10 p-3">
              {[["moving", t.nav.indMoving, t.nav.indMovingHint], ["living", t.nav.indLiving, t.nav.indLivingHint]].map(([k, a, b]) => (
                <Link key={k} to={link(k)} onClick={() => setOpen(false)} data-testid={`nav-individuals-${k}`} className="group flex items-start justify-between gap-3 rounded-2xl p-4 transition-colors hover:bg-ivory-2">
                  <span>
                    <span className="block font-display text-xl text-forest">{a}</span>
                    <span className="mt-1 block text-sm text-forest/70">{b}</span>
                  </span>
                  <ArrowRight size={16} className="mt-1.5 text-gold-text transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const useCta = () => {
  const { pathname } = useLocation();
  const t = useT();
  const link = useLink();
  const biz = link("business");
  const onBusiness = pathname === biz || pathname.startsWith(`${biz}/`);
  return onBusiness ? [t.business.cta, link("contact")] : [t.nav.cta, link("apply")];
};

const navItems = (t) => [
  ["home", t.nav.home], ["about", t.nav.about], ["services", t.nav.services],
  ["individuals", t.nav.individuals], ["business", t.nav.business], ["contact", t.nav.contact],
];

const MobileMenu = ({ onClose }) => {
  const t = useT();
  const link = useLink();
  const [ctaLabel, ctaTo] = useCta();
  const items = navItems(t).flatMap(([k, l]) => (k === "individuals" ? [["moving", t.nav.indMoving], ["living", t.nav.indLiving]] : [[k, l]]));
  return (
    <motion.div
      data-testid="mobile-menu"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex flex-col overflow-y-auto bg-forest px-6 pb-10 pt-5 text-ivory lg:hidden"
    >
      <div className="flex items-center justify-between">
        <Logo to={link("home")} light testid="mobile-menu-logo" />
        <button onClick={onClose} aria-label={t.nav.close} data-testid="mobile-menu-close" className="flex h-12 w-12 items-center justify-center rounded-full border border-ivory/25">
          <X size={22} />
        </button>
      </div>
      <nav className="mt-10 flex flex-col">
        {items.map(([k, l], i) => (
          <motion.div key={k} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
            <Link to={link(k)} onClick={onClose} data-testid={`mobile-nav-${k}`} className="block border-b border-ivory/10 py-4 font-display text-[2rem] leading-tight">
              {l}
            </Link>
          </motion.div>
        ))}
      </nav>
      <div className="mt-8 flex flex-col gap-4">
        <Link to={ctaTo} onClick={onClose} data-testid="mobile-cta-btn" className="btn btn-ivory w-full hover:!bg-gold-deep">{ctaLabel}</Link>
        <LangSwitch className="self-start border-ivory/30 [&_a]:text-ivory [&_a[aria-current]]:bg-ivory [&_a[aria-current]]:!text-forest" />
      </div>
    </motion.div>
  );
};

export const Header = () => {
  const t = useT();
  const link = useLink();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [ctaLabel, ctaTo] = useCta();
  const { pathname } = useLocation();

  useEffect(() => { setMobile(false); }, [pathname]);
  useEffect(() => {
    if (mobile) window.__lenis?.stop(); else window.__lenis?.start();
  }, [mobile]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${scrolled ? "bg-ivory/85 py-3 shadow-[0_10px_40px_-30px_rgba(23,56,46,.6)] backdrop-blur-xl" : "py-5"}`}
    >
      <div className="container-x flex items-center justify-between gap-6">
        <Logo to={link("home")} className={`w-auto transition-[height] duration-500 ${scrolled ? "h-[58px] sm:h-[66px]" : "h-[66px] sm:h-[86px]"}`} />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main">
          {navItems(t).map(([k, l]) =>
            k === "individuals" ? (
              <IndividualsMenu key={k} />
            ) : (
              <NavLink
                key={k}
                to={link(k)}
                end={k === "home"}
                data-testid={`nav-${k}`}
                className={({ isActive }) => `link-u py-2 text-[15px] transition-colors hover:text-gold-text ${isActive ? "text-gold-text" : "text-forest"}`}
              >
                {l}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <Link to={ctaTo} data-testid="header-cta-btn" className="btn btn-gold hidden !px-5 !py-3 text-sm xl:inline-flex">{ctaLabel}</Link>
          <LangSwitch className="hidden sm:flex" />
          <button onClick={() => setMobile(true)} aria-label={t.nav.menu} data-testid="mobile-menu-open" className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/20 text-forest lg:hidden">
            <Menu size={22} />
          </button>
        </div>
      </div>
      {createPortal(<AnimatePresence>{mobile && <MobileMenu onClose={() => setMobile(false)} />}</AnimatePresence>, document.body)}
    </header>
  );
};
